'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Service {
  id: number;
  name: string;
  icon: string;
  description: string;
  industries: string[];
  scale: string[];
  link: string;
}

const ServiceSelector = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');
  const [selectedScale, setSelectedScale] = useState<string>('all');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const services: Service[] = [
    {
      id: 1,
      name: "Telecom Tower Installation",
      icon: "SignalIcon",
      description: "End-to-end tower installation, commissioning, and integration services for telecom operators",
      industries: ["telecom"],
      scale: ["large", "medium"],
      link: "/services-overview"
    },
    {
      id: 2,
      name: "Electrical Systems",
      icon: "BoltIcon",
      description: "HT/LT installations, DG sets, UPS systems, and power distribution solutions",
      industries: ["power", "infrastructure"],
      scale: ["large", "medium", "small"],
      link: "/services-overview"
    },
    {
      id: 3,
      name: "AMC & Maintenance",
      icon: "WrenchScrewdriverIcon",
      description: "Comprehensive annual maintenance contracts with 24/7 support and preventive maintenance",
      industries: ["telecom", "power", "infrastructure"],
      scale: ["large", "medium", "small"],
      link: "/services-overview"
    },
    {
      id: 4,
      name: "Civil & Structural Works",
      icon: "BuildingOfficeIcon",
      description: "Foundation work, shelter construction, and structural modifications for infrastructure projects",
      industries: ["infrastructure"],
      scale: ["large", "medium"],
      link: "/services-overview"
    },
    {
      id: 5,
      name: "Network Integration",
      icon: "ServerStackIcon",
      description: "System integration, testing, and commissioning for telecom and IT infrastructure",
      industries: ["telecom"],
      scale: ["large", "medium"],
      link: "/services-overview"
    },
    {
      id: 6,
      name: "Emergency Response",
      icon: "ExclamationTriangleIcon",
      description: "2-hour emergency response for critical infrastructure failures and urgent repairs",
      industries: ["telecom", "power", "infrastructure"],
      scale: ["large", "medium", "small"],
      link: "/contact-and-support"
    }
  ];

  const industries = [
    { value: 'all', label: 'All Industries', icon: 'Squares2X2Icon' },
    { value: 'telecom', label: 'Telecom', icon: 'SignalIcon' },
    { value: 'power', label: 'Power & Electrical', icon: 'BoltIcon' },
    { value: 'infrastructure', label: 'Infrastructure', icon: 'BuildingOfficeIcon' }
  ];

  const scales = [
    { value: 'all', label: 'All Scales', icon: 'AdjustmentsHorizontalIcon' },
    { value: 'large', label: 'Large Scale', icon: 'BuildingOffice2Icon' },
    { value: 'medium', label: 'Medium Scale', icon: 'BuildingOfficeIcon' },
    { value: 'small', label: 'Small Scale', icon: 'HomeModernIcon' }
  ];

  const filteredServices = services.filter(service => {
    const industryMatch = selectedIndustry === 'all' || service.industries.includes(selectedIndustry);
    const scaleMatch = selectedScale === 'all' || service.scale.includes(selectedScale);
    return industryMatch && scaleMatch;
  });

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
              Find Your Solution
            </h2>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-primary mb-4">
            Find Your Solution
          </h2>
          <p className="text-lg text-textSecondary font-body max-w-3xl mx-auto">
            Filter our comprehensive service portfolio by industry and project scale to find the perfect solution for your infrastructure needs
          </p>
        </div>

        {/* Filter Controls */}
        <div className="mb-12 space-y-6">
          {/* Industry Filter */}
          <div>
            <label className="block text-sm font-body font-semibold text-textPrimary mb-3">
              Filter by Industry
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {industries.map((industry) => (
                <button
                  key={industry.value}
                  onClick={() => setSelectedIndustry(industry.value)}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg font-body font-medium transition-all duration-300 ${
                    selectedIndustry === industry.value
                      ? 'bg-primary text-primary-foreground shadow-subtle'
                      : 'bg-card text-textPrimary border border-border hover:border-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={industry.icon as any} size={20} className="mr-2" />
                  {industry.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scale Filter */}
          <div>
            <label className="block text-sm font-body font-semibold text-textPrimary mb-3">
              Filter by Project Scale
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {scales.map((scale) => (
                <button
                  key={scale.value}
                  onClick={() => setSelectedScale(scale.value)}
                  className={`flex items-center justify-center px-4 py-3 rounded-lg font-body font-medium transition-all duration-300 ${
                    selectedScale === scale.value
                      ? 'bg-primary text-primary-foreground shadow-subtle'
                      : 'bg-card text-textPrimary border border-border hover:border-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={scale.icon as any} size={20} className="mr-2" />
                  {scale.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-sm font-body text-textSecondary">
            Showing <span className="font-semibold text-primary">{filteredServices.length}</span> service{filteredServices.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className="bg-card rounded-lg border border-border p-6 transition-all duration-300 hover:shadow-subtle hover:border-primary group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                  <Icon name={service.icon as any} size={24} className="text-primary group-hover:text-primary-foreground" />
                </div>
                <Icon name="ArrowTopRightOnSquareIcon" size={20} className="text-textSecondary group-hover:text-primary transition-colors duration-300" />
              </div>

              <h3 className="text-xl font-headline font-bold text-primary mb-3 group-hover:text-secondary transition-colors duration-300">
                {service.name}
              </h3>

              <p className="text-sm text-textSecondary font-body mb-4 leading-relaxed">
                {service.description}
              </p>

              <a
                href={service.link}
                className="inline-flex items-center text-sm font-body font-semibold text-primary hover:text-secondary transition-colors duration-300"
              >
                Learn More
                <Icon name="ArrowRightIcon" size={16} className="ml-1" />
              </a>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredServices.length === 0 && (
          <div className="text-center py-12">
            <Icon name="MagnifyingGlassIcon" size={48} className="text-textSecondary mx-auto mb-4" />
            <h3 className="text-xl font-headline font-bold text-textPrimary mb-2">
              No Services Found
            </h3>
            <p className="text-textSecondary font-body mb-6">
              Try adjusting your filters to see more results
            </p>
            <button
              onClick={() => {
                setSelectedIndustry('all');
                setSelectedScale('all');
              }}
              className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-md font-body font-semibold transition-all duration-300 hover:bg-secondary"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceSelector;